import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import './index.scss';

import {
    closestCenter,
    DndContext,
    DragOverlay,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import { restrictToHorizontalAxis } from '@dnd-kit/modifiers';
import {
    arrayMove,
    horizontalListSortingStrategy,
    SortableContext,
    useSortable,
} from '@dnd-kit/sortable';
import { IconSearch } from "delivapp-ordering";
import CustomTable from "./CustomTable.jsx";
import TableFilterModal from "./TableFilterModal.jsx";
import ColumnFilterDropdown from "./ColumnFilterDropdown.jsx";
import { getTableStoreData, setTableStoreData } from "../../../utils/tableStore.js";
import { useDispatch } from "react-redux";
import GlobalFilterDataService from "../../../services/globalFilterDataService.js";

const DragIndexContext = createContext({
    active: null,
    over: null,
});

const dragActiveStyle = (dragState, accessor) => {
    const { active, over, direction } = dragState;
    let style = {};
    if (active && active === accessor) {
        style = {
            backgroundColor: 'gray',
            opacity: 0.5,
        };
        // dragover dashed style
    } else if (over && accessor === over && active !== over) {
        style =
            direction === 'right'
                ? {
                    borderRight: '1px dashed gray',
                }
                : {
                    borderLeft: '1px dashed gray',
                };
    }
    return style;
};

const TableBodyCell = (props) => {
    const dragState = useContext(DragIndexContext);
    return (
        <td
            {...props}
            style={{
                ...props.style,
                ...dragActiveStyle(dragState, props.id),
            }}
        />
    );
};

const TableHeaderCell = (props) => {
    const dragState = useContext(DragIndexContext);
    const { attributes, listeners, setNodeRef, isDragging } = useSortable({
        id: props.id,
    });
    const style = {
        ...props.style,
        cursor: 'move',
        ...(isDragging
            ? {
                position: 'relative',
                zIndex: 9999,
                userSelect: 'none',
            }
            : {}),
        ...dragActiveStyle(dragState, props.id),
    };
    return <th {...props} ref={setNodeRef} style={style} {...attributes} {...listeners} />;
};

const CustomTableWrapper = ({
    t,
    data,
    id,
    columns,
    loading,
    onChange,
    filter,
    onSetFilter,
    handleOnSaveReport,
    onRefreshData,
    setSearchValue,
    searchValue,
    filters,
    pagination,
    onSetTableFilter,
    onSetTablePagination,
    keyForDate,
    summaryComponent,
    reportComponent,
    addComponent,
    customTableConfig,
    onSelectRow,
    periodControl,
    showAutoRefresh,
    isAutoRefresh,
}) => {
    const dispatch = useDispatch();
    const filtersRef = useRef(filters);
    useEffect(() => {
        filtersRef.current = filters;
    }, [filters])
    const [columnVisible, setColumnVisible] = useState(
        columns
            .filter((c) => !c.visible && c.canHideColumn && !c.alwaysHidden)
            .map((c) => c.accessor)
    );
    const [openFilterByAccessor, setOpenFilterByAccessor] = useState(false)

    const isVisible = (col) => {
        if (!columnVisible.length || !col.canHideColumn) {
            return true;
        }
        return columnVisible.includes(col.accessor);
    };

    const handleOnChangeSort = (col) => {
        if (!col.enableSortColumn) {
            return;
        }
        const newFilter = { ...filtersRef.current };
        const sortOrder = newFilter[col.accessor]?.sort || 0;

        Object.entries(newFilter).forEach(([key, value]) => {
            if (value.operator) {
                newFilter[key] = { ...newFilter[key], sort: 0 };
            } else {
                delete newFilter[key];
            }
        });

        newFilter[col.accessor] = {
            ...(newFilter[col.accessor] || {}),
            sort: sortOrder === 0
                ? 1
                : sortOrder === 1
                    ? -1
                    : 0,
        };
        onSetTableFilter(newFilter)
        onRefreshData(newFilter, { pageOrSortChanged: true });
    };

    const [dataColumns, setDataColumns] = useState(() =>
        columns.map((col, i) => ({
            ...col,
            dataIndex: col.accessor,
            key: col.accessor,
            onHeaderCell: column => ({
                id: col.accessor,
                onClick: () => handleOnChangeSort(column),
            }),
            onCell: () => ({ id: col.accessor }),
            index: i,
        }))
    );

    const [dragIndex, setDragIndex] = useState({
        active: null,
        over: null,
        direction: null,
    });

    const [filteredData, setFilteredData] = useState(data);

    const [isAddFilterVisible, setIsAddFilterVisible] = useState(false);

    useEffect(() => {
        setFilteredData(data);
    }, [data, filters, dataColumns]);

    useEffect(() => {
        const tableData = getTableStoreData(id);
        if (tableData?.order) {
            setDataColumns((prevCols) =>
                prevCols
                    .map((col) => {
                        const found = tableData.order.find((f) => f.accessor === col.accessor);
                        return found ? { ...col, index: found.index } : col;
                    })
                    .sort((a, b) => a.index - b.index)
            );
        }
        if (tableData?.columnVisible) {
            setColumnVisible(tableData.columnVisible);
        }
    }, [id]);

    const setFilters = (data) => {
        const filter = {};
        for (let accessor in data) {
            if ((data[accessor].type === "lookup" && data[accessor].value.length) || data[accessor].type !== "lookup") {
                filter[accessor] = data[accessor];
            }
        }
        onSetTablePagination({ page: 0, pageSize: pagination.pageSize });
        onSetTableFilter(filter);
        onRefreshData(filter, { pageSize: pagination.pageSize, page: 0 });
    }

    const setPagination = (data) => {
        onRefreshData(filters, data);
        onSetTablePagination(data);
    }

    const handleOnChangeVisible = (newHidden) => {
        setTableStoreData(id, { columnVisible: newHidden });
        setColumnVisible(newHidden);
    };

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 1,
            },
        })
    );

    const onDragOver = ({ active, over }) => {
        if (!active || !over) return;
        const activeIndex = dataColumns.findIndex((col) => col.accessor === active.id);
        const overIndex = dataColumns.findIndex((col) => col.accessor === over.id);

        setDragIndex({
            active: active.id,
            over: over.id,
            direction: overIndex > activeIndex ? "right" : "left",
        });
    };

    const onDragEnd = ({ active, over }) => {
        if (active?.id === "actions" || !active || !over) {
            setDragIndex({ active: null, over: null, direction: null });
            return;
        }
        if (active.id !== over.id) {
            setDataColumns((prev) => {
                const activeIndex = prev.findIndex((col) => col.accessor === active.id);
                const overIndex = prev.findIndex((col) => col.accessor === over.id);
                const newArr = arrayMove(prev, activeIndex, overIndex);

                setTableStoreData(id, {
                    order: newArr.map((col, i) => ({ accessor: col.accessor, index: i })),
                });

                return newArr;
            });
        }
        setDragIndex({ active: null, over: null, direction: null });
    };

    const getColumnSearchProps = (accessor, coll) => {
        if (!coll?.type || accessor === 'actions') {
            return {};
        }

        return {
            filterDropdown: ({ confirm, close }) => (
                <ColumnFilterDropdown
                    accessor={accessor}
                    filter={filter}
                    coll={coll}
                    confirm={confirm}
                    close={close}
                    filters={filters}
                    isOpenByAccessor={openFilterByAccessor?.accessor === coll.accessor && openFilterByAccessor?.isOpen}
                    setFilters={(data) => setFilters({ ...filters, ...data })}
                    t={t}
                />
            ),
            filterDropdownProps: {
                onOpenChange(open) {
                    setTimeout(() => {
                        setOpenFilterByAccessor({ accessor: coll.accessor, isOpen: open });
                    }, 0);
                },
            },
            filterIcon: (filtered) => (
                <IconSearch size={"15px"} color={filtered ? "#007aff" : "#000"} />
            ),
            filtered: !!filters[accessor],
            onFilter: () => true,
        }
    };

    const handleTableChange = (tableFilters, extra) => {
        setFilters(tableFilters);
        if (extra.action === "filter") {
            onChange && onChange(extra.currentDataSource);
        }
    };

    const handleRemoveFilter = (accessor) => {
        const newFilters = { ...filters };
        const findColumn = columns.find(c => c.accessor === accessor);
        if (findColumn?.lookupOnApply) {
            dispatch(findColumn.lookupOnApply([]))
        }
        delete newFilters[accessor];
        setFilters(newFilters);
    };

    const handleRemoveAll = () => {
        const mustHaveFilter = finalColumns.filter(column => column.alwaysOnFilter);
        const newFilter = {};
        mustHaveFilter.forEach(column => {
            if (filters[column.accessor]) {
                newFilter[column.accessor] = filters[column.accessor];
            }
        })
        setFilters(newFilter);
    };

    // Back to the configured starting point: the table chips go to customTableConfig.defaultFilters
    // and the period goes to the default of customTableConfig.filterType. alwaysOnFilter columns
    // survive, because they carry scope the user never chose (the consumer id on a customer's
    // orders) rather than a filter they applied — same rule handleRemoveAll follows.
    const handleResetFilters = () => {
        const defaultFilters = { ...(customTableConfig?.defaultFilters || {}) };
        finalColumns
            .filter(column => column.alwaysOnFilter && !defaultFilters[column.accessor])
            .forEach(column => {
                if (filters[column.accessor]) {
                    defaultFilters[column.accessor] = filters[column.accessor];
                }
            });

        Object.keys(filters)
            .filter(accessor => !defaultFilters[accessor])
            .forEach(accessor => {
                const column = columns.find(c => c.accessor === accessor);
                if (column?.lookupOnApply) {
                    dispatch(column.lookupOnApply([]));
                }
            });

        const defaultPeriodFilter = customTableConfig?.filterType
            ? GlobalFilterDataService.instance.resetFilter(customTableConfig.filterType)
            : null;

        onSetTablePagination({ page: 0, pageSize: pagination.pageSize });

        if (defaultPeriodFilter && onSetFilter) {
            // onSetFilter writes the period, the chips and refetches in one pass. It only pushes
            // the chips itself when the default period carries dates (orderByCustomer's does not),
            // so they are written here first and it re-writes them with its own date filter merged.
            onSetTableFilter({ ...defaultFilters });
            onSetFilter(defaultPeriodFilter, { ...defaultFilters });
            return;
        }

        onSetTableFilter({ ...defaultFilters });
        onRefreshData({ ...defaultFilters }, { pageSize: pagination.pageSize, page: 0 });
    };

    const handleAddFilter = () => {
        setIsAddFilterVisible(true);
    };

    const handleApplyNewFilter = (filterData) => {
        setFilters({
            ...filters,
            [filterData.accessor]: {
                operator: filterData.operator,
                value: filterData.value ?? "",
                value2: filterData.value2 ?? "",
                type: filterData.type || "",
            },
        });
    };

    const finalColumns = dataColumns.map((col) => ({
        ...col,
        dataIndex: col.accessor,
        hidden: !isVisible(col),
        ...(col.search ? getColumnSearchProps(col.accessor, col) : {}),
    }));

    return (
        <div className="customTableWrapper">
            <DndContext
                sensors={sensors}
                modifiers={[restrictToHorizontalAxis]}
                onDragEnd={onDragEnd}
                onDragOver={onDragOver}
                collisionDetection={closestCenter}
            >
                <SortableContext
                    items={dataColumns.map((col) => col.accessor)}
                    strategy={horizontalListSortingStrategy}
                >
                    <DragIndexContext.Provider value={dragIndex}>
                        <CustomTable
                            showAutoRefresh={showAutoRefresh}
                            isAutoRefresh={isAutoRefresh}
                            hideDatePredefinedValue={customTableConfig?.hideDatePredefinedValue}
                            reportComponent={reportComponent}
                            addComponent={addComponent}
                            onSelectRow={onSelectRow}
                            summaryComponent={summaryComponent}
                            searchValue={searchValue}
                            setSearchValue={setSearchValue}
                            t={t}
                            data={filteredData}
                            loading={loading}
                            columns={columns.filter((c) => c.canHideColumn && (c.isShow ? c.isShow(data) : true))}
                            dataColumns={finalColumns}
                            columnVisible={columnVisible}
                            setColumnVisible={handleOnChangeVisible}
                            components={{
                                header: {
                                    cell: TableHeaderCell,
                                },
                                body: {
                                    cell: TableBodyCell,
                                },
                            }}
                            filter={filter}
                            onSetFilter={data => onSetFilter(data, filters)}
                            handleOnSaveReport={handleOnSaveReport}
                            periodControl={periodControl}
                            filters={filters}
                            handleOnChange={handleTableChange}
                            handleRemoveFilter={handleRemoveFilter}
                            handleRemoveAll={handleRemoveAll}
                            handleResetFilters={handleResetFilters}
                            resetFiltersConfig={customTableConfig?.resetFiltersButton}
                            handleAddFilter={handleAddFilter}
                            pagination={pagination}
                            setPagination={setPagination}
                        />
                    </DragIndexContext.Provider>
                </SortableContext>
                <DragOverlay>
                    <th style={{ backgroundColor: "gray", padding: 16 }}>
                        {
                            dataColumns.find((col) => col.accessor === dragIndex.active)
                                ?.title
                        }
                    </th>
                </DragOverlay>
            </DndContext>
            <TableFilterModal
                filter={filter}
                filters={filters}
                visible={isAddFilterVisible}
                onClose={() => setIsAddFilterVisible(false)}
                dataColumns={finalColumns}
                onApply={handleApplyNewFilter}
                t={t}
            />
        </div>
    );
};

export default CustomTableWrapper;
